import { isInwardSupplyRow } from "../../../services/billingImportFindService";
import {
  advancedReportLineAmount,
  loadLineChunkRowsForDateRange,
  normalizeVoucherType,
} from "./salesLineChunksReader";
import {
  normalizeSalesImportBranchId,
  salesImportBranchDisplayName,
  sortSalesImportBranchIds,
} from "../config/salesImportBranchIds";
import { rowTranDateYmd } from "../utils/dateParse";

export function brandNameFromRow(row) {
  return String(
    row?.brandName ?? row?.BrandName ?? row?.brandname ?? row?.brand ?? row?.["Brand Name"] ?? ""
  ).trim();
}

function branchIdFromRow(row) {
  return normalizeSalesImportBranchId(row.branchId ?? row.BranchID ?? row.branch ?? "") || "Unknown";
}

function rowMatchesVoucherType(row, selectedVoucherType) {
  if (!selectedVoucherType || selectedVoucherType === "all") return true;
  return normalizeVoucherType(row) === selectedVoucherType;
}

export function pickAmount(cell, amountType) {
  if (amountType === "purchase") return cell.purchaseAmount;
  if (amountType === "net") return cell.salesAmount - cell.purchaseAmount;
  return cell.salesAmount;
}

/**
 * Aggregate import lines by BrandName column × branch.
 * @param {object[]} rows
 * @param {{ year?: number, branchIds?: string[], amountType?: 'sales'|'purchase'|'net', voucherType?: string }} [opts]
 */
export function aggregateBrandByBranch(rows, opts = {}) {
  const { year, branchIds = [], amountType = "sales", voucherType = "all" } = opts;

  const branchSet = new Set(
    (branchIds || []).map((id) => normalizeSalesImportBranchId(id)).filter(Boolean)
  );
  const filterBranches = branchSet.size > 0;
  const cellMap = new Map();
  const brandSet = new Set();
  const branchIdsFound = new Set();
  const voucherTypeOptions = new Set();
  let rowsWithBrandName = 0;
  let rowsWithoutBrandName = 0;
  let lineCount = 0;

  for (const row of rows || []) {
    const ymd = rowTranDateYmd(row);
    if (!ymd) continue;
    if (year != null && Number(ymd.slice(0, 4)) !== year) continue;

    const branch = branchIdFromRow(row);
    if (filterBranches && !branchSet.has(branch)) continue;

    voucherTypeOptions.add(normalizeVoucherType(row));
    if (!rowMatchesVoucherType(row, voucherType)) continue;

    const rawBrand = brandNameFromRow(row);
    if (rawBrand) rowsWithBrandName += 1;
    else rowsWithoutBrandName += 1;

    const brand = rawBrand || "(Blank)";
    const amt = advancedReportLineAmount(row);
    const isPurchase = isInwardSupplyRow(row);
    const cellKey = `${brand}::${branch}`;

    const prev = cellMap.get(cellKey) || {
      key: brand,
      branch,
      salesAmount: 0,
      purchaseAmount: 0,
      lineCount: 0,
    };
    prev.salesAmount += amt;
    if (isPurchase) prev.purchaseAmount += amt;
    prev.lineCount += 1;
    cellMap.set(cellKey, prev);

    brandSet.add(brand);
    branchIdsFound.add(branch);
    lineCount += 1;
  }

  const branches = sortSalesImportBranchIds([...branchIdsFound]).map((id) => ({
    id,
    label: salesImportBranchDisplayName(id),
  }));

  const tableRows = [...brandSet]
    .sort((a, b) => a.localeCompare(b))
    .map((brand) => {
      const byBranch = {};
      let totalSales = 0;
      let totalPurchase = 0;
      let brandLineCount = 0;
      for (const br of branches) {
        const c = cellMap.get(`${brand}::${br.id}`) || { salesAmount: 0, purchaseAmount: 0, lineCount: 0 };
        byBranch[br.id] = { salesAmount: c.salesAmount, purchaseAmount: c.purchaseAmount };
        totalSales += c.salesAmount;
        totalPurchase += c.purchaseAmount;
        brandLineCount += c.lineCount || 0;
      }
      return {
        key: brand,
        label: brand,
        byBranch,
        totalSales,
        totalPurchase,
        totalNet: totalSales - totalPurchase,
        lineCount: brandLineCount,
        isBlank: brand === "(Blank)",
      };
    });

  const branchTotals = {};
  for (const br of branches) {
    let salesAmount = 0;
    let purchaseAmount = 0;
    for (const r of tableRows) {
      salesAmount += r.byBranch[br.id]?.salesAmount || 0;
      purchaseAmount += r.byBranch[br.id]?.purchaseAmount || 0;
    }
    branchTotals[br.id] = { salesAmount, purchaseAmount, net: salesAmount - purchaseAmount };
  }

  const grandSales = Object.values(branchTotals).reduce((s, t) => s + t.salesAmount, 0);
  const grandPurchase = Object.values(branchTotals).reduce((s, t) => s + t.purchaseAmount, 0);

  return {
    rows: tableRows,
    branches,
    branchTotals,
    grandSales,
    grandPurchase,
    grandNet: grandSales - grandPurchase,
    lineCount,
    brandCount: tableRows.length,
    rowsWithBrandName,
    rowsWithoutBrandName,
    amountType,
    voucherType,
    voucherTypeOptions: [...voucherTypeOptions].sort((a, b) => a.localeCompare(b)),
  };
}

/**
 * @param {{ year: number, branchIds?: string[], amountType?: string, voucherType?: string }} params
 * @param {(n: number) => void} [onProgress]
 */
export async function fetchBrandByBranchReport(params, onProgress) {
  const year = Number(params?.year);
  if (!Number.isFinite(year)) throw new Error("Select a valid year.");

  const fromDate = `${year}-01-01`;
  const toDate = `${year}-12-31`;
  const { rows } = await loadLineChunkRowsForDateRange({ fromDate, toDate }, (n) => {
    onProgress?.(n);
  });

  return {
    ...aggregateBrandByBranch(rows, {
      year,
      branchIds: params.branchIds,
      amountType: params.amountType || "sales",
      voucherType: params.voucherType || "all",
    }),
    year,
    fromDate,
    toDate,
  };
}

export function flattenBrandByBranchForExport(report) {
  const branches = report?.branches || [];
  const amountType = report?.amountType || "sales";
  const amountLabel =
    amountType === "purchase" ? "Purchase" : amountType === "net" ? "Net" : "Sales";

  return (report?.rows || []).map((row) => {
    const out = {
      BrandName: row.label,
      Lines: row.lineCount || 0,
      [`Total ${amountLabel}`]: pickAmount(
        { salesAmount: row.totalSales, purchaseAmount: row.totalPurchase },
        amountType
      ),
    };
    for (const br of branches) {
      const cell = row.byBranch[br.id] || { salesAmount: 0, purchaseAmount: 0 };
      out[br.label] = pickAmount(cell, amountType);
    }
    return out;
  });
}
